import { useCachedMembers } from '@easy-expense/data-firestore-client';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon, IconName } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { getUserDisplayName } from '@easy-expense/utils-shared';
import React from 'react';

import { Button } from '../Button.components';
import { MultiSelectFilter } from '../Filters/MultiSelectFilter.component';
import { LabelTextField } from '../LabelTextField.component';
import { AutoCompleteValue } from '../Shared/Dropdown/AutoCompleteTypes';
import { Modal } from '../Shared/Modal.component';

export const ReconcileModal: React.FC<
  React.PropsWithChildren<{
    showModal: boolean;
    setShowModal: (showModal: boolean) => void;
    onReconcile: () => void;
    dateHeader: string;
    setDateHeader: (date: string) => void;
    totalHeader: string;
    setTotalHeader: (total: string) => void;
    headers: string[];
    statementName: string;
    setStatementName: (name: string) => void;
    assignedTo: string[];
    setAssignedTo: (userIDs: string[]) => void;
  }>
> = (props) => {
  const {
    showModal,
    setShowModal,
    onReconcile,
    dateHeader,
    setDateHeader,
    totalHeader,
    setTotalHeader,
    headers,
    statementName,
    setStatementName,
    assignedTo,
    setAssignedTo,
  } = props;

  const dateSelectRef = React.useRef<HTMLSelectElement>(null);
  const totalSelectRef = React.useRef<HTMLSelectElement>(null);

  const members = useCachedMembers();

  const membersValues = React.useMemo<AutoCompleteValue[]>(() => {
    return members.map((member) => ({
      key: member.key,
      value: {
        name: getUserDisplayName(member, ''),
        image: member.profileImage,
        iconicIcon: 'person-circle-outline' as IconName,
      },
    }));
  }, [members]);

  return (
    <Modal
      showModal={showModal}
      setShowModal={setShowModal}
      footer={
        <Layout.Column
          style={{
            borderTop: `2px solid ${theme.colors.grayXLight}`,
          }}
        >
          <Layout.Row px={32} py={24} justify="flex-end">
            <Button.Primary onClick={onReconcile} content={getTranslation('Reconcile')} />
          </Layout.Row>
        </Layout.Column>
      }
    >
      <Layout.Column style={{ maxWidth: 700 }}>
        <OpenSans.Primary size={36} weight="bold-700">
          {getTranslation('Reconcile Statement')}
        </OpenSans.Primary>

        <Spacer.Vertical size={24} />

        <Layout.Row
          align
          style={{
            borderTop: `1px solid ${theme.colors.inputBorder}`,
          }}
          px
          py
        >
          <Layout.Column
            style={{
              maxWidth: 350,
            }}
          >
            <Layout.Row align>
              <OpenSans.Primary size={16} weight="bold-700">
                {getTranslation('Date')}
              </OpenSans.Primary>
            </Layout.Row>
            <Layout.Row align>
              <OpenSans.Secondary size={16}>
                {getTranslation(
                  'Pick the column from your statement that corresponds to the date of the receipts',
                )}
              </OpenSans.Secondary>
            </Layout.Row>
          </Layout.Column>
          <Spacer.Horizontal size={24} />
          <Layout.Column>
            <LabelTextField label="" active={false}>
              <Layout.Row
                style={{
                  width: '100%',
                  fontFamily: 'Poppins_Light',
                  position: 'relative',
                }}
              >
                <span
                  style={{ cursor: 'pointer' }}
                  onClick={() => dateSelectRef.current?.showPicker()}
                >
                  <Icon name="calendar-outline" size={20} style={{ paddingRight: 10 }} />
                </span>
                <select
                  ref={dateSelectRef}
                  value={dateHeader}
                  onChange={(e) => setDateHeader(e.target.value)}
                  style={{
                    border: 'none',
                    width: '100%',
                    background: 'transparent',
                    zIndex: 1,
                    cursor: 'pointer',
                  }}
                >
                  <option value="">Select date column</option>
                  {headers.map((header) => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </select>

                <span
                  style={{ cursor: 'pointer' }}
                  onClick={() => dateSelectRef.current?.showPicker()}
                >
                  <Icon name="chevron-down" size={20} style={{ paddingLeft: 10 }} />
                </span>
              </Layout.Row>
            </LabelTextField>
          </Layout.Column>
        </Layout.Row>

        <Layout.Row
          align
          style={{
            borderTop: `1px solid ${theme.colors.inputBorder}`,
          }}
          px
          py
        >
          <Layout.Column
            style={{
              maxWidth: 350,
            }}
          >
            <Layout.Row align>
              <OpenSans.Primary size={16} weight="bold-700">
                {getTranslation('Total')}
              </OpenSans.Primary>
            </Layout.Row>
            <Layout.Row align>
              <OpenSans.Secondary size={16}>
                {getTranslation(
                  'Pick the column from your statement that corresponds to the expense total of the receipts',
                )}
              </OpenSans.Secondary>
            </Layout.Row>
          </Layout.Column>
          <Spacer.Horizontal size={24} />
          <Layout.Column>
            <LabelTextField label="" active={false}>
              <Layout.Row
                style={{
                  width: '100%',
                  fontFamily: 'Poppins_Light',
                  position: 'relative',
                }}
              >
                <span
                  style={{ cursor: 'pointer' }}
                  onClick={() => totalSelectRef.current?.showPicker()}
                >
                  <Icon name="calculator-outline" size={20} style={{ paddingRight: 10 }} />
                </span>
                <select
                  ref={totalSelectRef}
                  value={totalHeader}
                  onChange={(e) => setTotalHeader(e.target.value)}
                  style={{
                    border: 'none',
                    width: '100%',
                    background: 'transparent',
                    zIndex: 1,
                    cursor: 'pointer',
                  }}
                >
                  <option value="">Select total column</option>
                  {headers.map((header) => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </select>

                <span
                  style={{ cursor: 'pointer' }}
                  onClick={() => totalSelectRef.current?.showPicker()}
                >
                  <Icon name="chevron-down" size={20} style={{ paddingLeft: 10 }} />
                </span>
              </Layout.Row>
            </LabelTextField>
          </Layout.Column>
        </Layout.Row>

        <Layout.Row
          align
          style={{
            borderTop: `1px solid ${theme.colors.inputBorder}`,
          }}
          px
          py
        >
          <Layout.Column
            style={{
              maxWidth: 350,
            }}
          >
            <Layout.Row align>
              <OpenSans.Primary size={16} weight="bold-700">
                {getTranslation('Statement Name')}
              </OpenSans.Primary>
            </Layout.Row>
            <Layout.Row align>
              <OpenSans.Secondary size={16}>
                {getTranslation('Optional name for this statement (defaults to filename)')}
              </OpenSans.Secondary>
            </Layout.Row>
          </Layout.Column>
          <Spacer.Horizontal size={24} />
          <Layout.Column>
            <LabelTextField label="" active={false}>
              <input
                type="text"
                value={statementName}
                onChange={(e) => setStatementName(e.target.value)}
                placeholder={getTranslation('Statement Name')}
                style={{
                  border: 'none',
                  width: '100%',
                  background: 'transparent',
                  fontFamily: 'Poppins_Light',
                  fontSize: 16,
                  outline: 'none',
                }}
              />
            </LabelTextField>
          </Layout.Column>
        </Layout.Row>

        <Layout.Row
          align
          style={{
            borderTop: `1px solid ${theme.colors.inputBorder}`,
          }}
          px
          py
        >
          <Layout.Column
            style={{
              maxWidth: 350,
            }}
          >
            <Layout.Row align>
              <OpenSans.Primary size={16} weight="bold-700">
                {getTranslation('Assign To')}
              </OpenSans.Primary>
            </Layout.Row>
            <Layout.Row align>
              <OpenSans.Secondary size={16}>
                {getTranslation('Optional: Assign this statement to a workspace member')}
              </OpenSans.Secondary>
            </Layout.Row>
          </Layout.Column>
          <Spacer.Horizontal size={24} />
          <Layout.Column>
            <MultiSelectFilter
              header=""
              placeholder={getTranslation('Select Members')}
              iconicIcon="people-circle-outline"
              selected={assignedTo}
              autoCompleteValues={membersValues}
              setSelected={setAssignedTo}
            />
          </Layout.Column>
        </Layout.Row>
      </Layout.Column>
    </Modal>
  );
};
